import { type FoundationVerticalDatumAbstraction } from "building-types/datums/datumAbstractionTypes";
import type { FoundationVerticalDatumEntity } from "building-types/datums/datumEntityTypes";
import { FoundationDatumVerticalAnchor } from "building-types/datums/datumsTypesEnum";
import type { FloorPath } from "building-types/floors/floorTypes";
import type { SheathingComponentEntity } from "building-types/floors/sheathingComponentTypes";
import type { SlabComponentEntity } from "building-types/floors/slabComponentTypes";
import type { StemWallEntity, StripFootingEntity } from "building-types/foundations/foundationComponentEntityTypes";
import { FoundationComponentType } from "building-types/foundations/foundationComponentEntityTypes";
import type { FoundationCondition } from "building-types/foundations/foundationEnums";
import { BrickLedgeType, SlabType, StemWallType } from "building-types/foundations/foundationEnums";
import type {
  FoundationExtrusionSize,
  FoundationPath,
  FoundationSection,
  FoundationSectionOpeningDescriptor,
  FoundationSectionSize,
  IsolatedFoundationSectionSize,
} from "building-types/foundations/foundationTypes";
import { FoundationSectionType } from "building-types/foundations/foundationTypes";
import type { FramingComponentEntity } from "building-types/framing/framingComponentTypes";
import type { Level } from "building-types/levels/levelTypes";
import { RoomClass } from "building-types/rooms/roomTypesEnum";
import type { DoorSettings } from "building-types/settings/doorSettings";
import type { SlabSettings } from "building-types/settings/foundationSettingsTypes";
import type { Table } from "common/data/table";
import { assertUnreachable } from "common/utils/assertUnreachable";
import { worldInchesNew } from "common/utils/nominalNumbers";
import { foundationVerticalDatumEntityNew } from "doc/datums/datumEntityNew";
import {
  isGarageFoundationCondition,
  isPorchFoundationCondition,
  isDeckFoundationCondition,
  isSpanFoundationCondition,
  isBasementFoundationCondition,
  isInteriorFoundationCondition,
} from "doc/utils/foundationSystemTools";
import { DIST_EPSILON } from "geom/core";
import type { PolygonPlanarGraph } from "geom/polygonPlanarGraph";
import { createDatumAbstraction } from "../datums/datumAbstractionFactory";
import { mergeDatumOffset } from "../datums/datumEntity";
import { defaultFoundationDatumAbstractions } from "../datums/defaultDatumAbstractions";
import { floorPathTop } from "../floor/floorPathUtils";
import { brickLedgeTypeAtCondition, makeBrickLedgeSize } from "./brickLedge";
import { foundationPathSuperSystemBottom } from "./foundationFloorPathUtils";
import type { FoundationPathEnds } from "./foundationStrip";
import {
  FoundationSectionAlignment,
  foundationExtrusionSizeNew,
  foundationPathFloorPerimeterEnds,
  foundationPathOffsetAlignment,
  foundationPathOpenings,
  foundationSectionId,
  linearFoundationSectionSizeFromAlignment,
  voidFoundationSectionSizeNew,
} from "./foundationStrip";
import { slabDepthAtFoundationPath, slabTypeAtFoundationPath } from "./slabUtils";

export function makeStemWallSectionSizes({
  level,
  foundationPath,
  floorPath,
  pathEnds,
  stemWall,
  footing,
  slabSettings,
  doorSettings,
  framingComponents,
  slabComponents,
  sheathingComponents,
  isolatedFoundationSectionSizes,
  planarGraphs,
}: {
  level: Level;
  foundationPath: FoundationPath;
  floorPath?: FloorPath;
  pathEnds: FoundationPathEnds;
  stemWall: StemWallEntity;
  footing: StripFootingEntity;
  slabSettings: SlabSettings | undefined;
  doorSettings: DoorSettings;
  framingComponents: Table<FramingComponentEntity>;
  slabComponents: Table<SlabComponentEntity>;
  sheathingComponents: Table<SheathingComponentEntity>;
  isolatedFoundationSectionSizes: IsolatedFoundationSectionSize[];
  planarGraphs: Table<PolygonPlanarGraph>;
}) {
  // below slab
  const stemWallComponentSize = makeStemWallComponentSize({
    level,
    foundationPath,
    floorPath,
    pathEnds,
    stemWall,
    footing,
    slabSettings,
    doorSettings,
    framingComponents,
    slabComponents,
    sheathingComponents,
    isolatedFoundationSectionSizes,
    planarGraphs,
  });

  // above slab
  const stemWallInsetSize = makeStemWallInsetSize({
    level,
    foundationPath,
    floorPath,
    pathEnds,
    stemWall,
    footing,
    slabSettings,
    doorSettings,
    framingComponents,
    slabComponents,
    sheathingComponents,
    isolatedFoundationSectionSizes,
    planarGraphs,
  });

  const brickLedgeSize = makeStemWallBrickLedgeSize({
    level,
    foundationPath,
    floorPath,
    pathEnds,
    stemWall,
    footing,
    doorSettings,
    slabSettings,
    isolatedFoundationSectionSizes,
    planarGraphs,
  });

  return [stemWallComponentSize, stemWallInsetSize, brickLedgeSize];
}

function makeStemWallComponentSize({
  level,
  foundationPath,
  floorPath,
  pathEnds,
  stemWall,
  footing,
  slabSettings,
  doorSettings,
  framingComponents,
  slabComponents,
  sheathingComponents,
  isolatedFoundationSectionSizes,
  planarGraphs,
}: {
  level: Level;
  foundationPath: FoundationPath;
  floorPath?: FloorPath;
  pathEnds: FoundationPathEnds;
  stemWall: StemWallEntity;
  footing: StripFootingEntity;
  slabSettings: SlabSettings | undefined;
  doorSettings: DoorSettings;
  framingComponents: Table<FramingComponentEntity>;
  slabComponents: Table<SlabComponentEntity>;
  sheathingComponents: Table<SheathingComponentEntity>;
  isolatedFoundationSectionSizes: IsolatedFoundationSectionSize[];
  planarGraphs: Table<PolygonPlanarGraph>;
}): FoundationSectionSize {
  const { condition } = foundationPath;

  const sectionType = FoundationSectionType.COMPONENT;
  const sectionId = stemWallSectionId(stemWall.componentType, sectionType, condition);

  const { start, end } = foundationPathFloorPerimeterEnds({
    foundationPath,
    floorPath,
    ends: pathEnds,
    planarGraphs,
  });
  const { offset, alignment } = foundationPathOffsetAlignment({
    foundationPath,
    floorPath,
  });
  const { openings } = stemWallOpenings({
    foundationPath,
    floorPath,
    doorSettings,
    isolatedFoundationSectionSizes,
    planarGraphs,
  });
  const { width } = stemWallWidth({ path: foundationPath, stemWall });
  const { material } = stemWall;

  const stemWallFull = makeStemWallExtrusionSize({
    foundationPath,
    stemWall,
    level,
    slabSettings,
    footing,
  });

  /**
   * note there is no split if there is no floor path passed in here as the floor path is used to determine the split
   */
  const { bottomSplit } = splitStemWallAtBOSuperSystem(
    level,
    foundationPath,
    stemWallFull,
    slabSettings,
    framingComponents,
    slabComponents,
    sheathingComponents,
    floorPath,
  );

  if (!bottomSplit) {
    return voidFoundationSectionSizeNew(stemWall, sectionId);
  }

  return linearFoundationSectionSizeFromAlignment({
    sectionComponent: stemWall,
    sectionType,
    sectionId,
    top: bottomSplit.top,
    depth: bottomSplit.depth,
    width,
    offset,
    start,
    end,
    alignment,
    openings,
    material,
  });
}

/**
 * Stem Wall insets exist because eventually the system will need to account for
 * Differing stem wall widths above and below a slab based on an inset value. Right now we use the same width,
 * but this is likely to change
 **/
function makeStemWallInsetSize({
  level,
  foundationPath,
  floorPath,
  pathEnds,
  stemWall,
  footing,
  slabSettings,
  doorSettings,
  framingComponents,
  slabComponents,
  sheathingComponents,
  isolatedFoundationSectionSizes,
  planarGraphs,
}: {
  level: Level;
  foundationPath: FoundationPath;
  floorPath?: FloorPath;
  pathEnds: FoundationPathEnds;
  stemWall: StemWallEntity;
  footing: StripFootingEntity;
  slabSettings: SlabSettings | undefined;
  doorSettings: DoorSettings;
  framingComponents: Table<FramingComponentEntity>;
  slabComponents: Table<SlabComponentEntity>;
  sheathingComponents: Table<SheathingComponentEntity>;
  isolatedFoundationSectionSizes: IsolatedFoundationSectionSize[];
  planarGraphs: Table<PolygonPlanarGraph>;
}): FoundationSectionSize {
  const { condition } = foundationPath;

  const sectionType = FoundationSectionType.INSET;
  const sectionId = stemWallSectionId(stemWall.componentType, sectionType, condition);

  const { start, end } = foundationPathFloorPerimeterEnds({
    foundationPath,
    floorPath,
    ends: pathEnds,
    planarGraphs,
  });
  const { offset, alignment } = foundationPathOffsetAlignment({
    foundationPath,
    floorPath,
  });
  const { openings } = stemWallOpenings({
    foundationPath,
    floorPath,
    doorSettings,
    isolatedFoundationSectionSizes,
    planarGraphs,
  });
  const { width } = stemWallWidth({ path: foundationPath, stemWall });
  const { material } = stemWall;

  const stemWallFull = makeStemWallExtrusionSize({
    foundationPath,
    stemWall,
    level,
    slabSettings,
    footing,
  });

  const { topSplit } = splitStemWallAtBOSuperSystem(
    level,
    foundationPath,
    stemWallFull,
    slabSettings,
    framingComponents,
    slabComponents,
    sheathingComponents,
    floorPath,
  );

  if (!topSplit) {
    return voidFoundationSectionSizeNew(stemWall, sectionId);
  }

  return linearFoundationSectionSizeFromAlignment({
    sectionComponent: stemWall,
    sectionType,
    sectionId,
    top: topSplit.top,
    depth: topSplit.depth,
    width,
    offset,
    start,
    end,
    alignment,
    openings,
    material,
  });
}

function makeStemWallBrickLedgeSize({
  level,
  foundationPath,
  floorPath,
  pathEnds,
  stemWall,
  footing,
  doorSettings,
  slabSettings,
  isolatedFoundationSectionSizes,
  planarGraphs,
}: {
  level: Level;
  foundationPath: FoundationPath;
  floorPath?: FloorPath;
  pathEnds: FoundationPathEnds;
  footing: StripFootingEntity;
  stemWall: StemWallEntity;
  doorSettings: DoorSettings;
  slabSettings: SlabSettings | undefined;
  isolatedFoundationSectionSizes: IsolatedFoundationSectionSize[];
  planarGraphs: Table<PolygonPlanarGraph>;
}): FoundationSectionSize {
  const { brickLedgeType, brickLedgeBehavior, material } = stemWall;
  const brickLedgeWidth = stemWallBrickLedgeWidth({ path: foundationPath, stemWall });
  const brickLedgeDepthModifier = level.floorIndex >= 0 ? -footing.footingDepth : 0;

  // the brick ledge should not extend past the top of the stem wall,
  // unless the stem wall is below a slab, in which case
  // the ledge should not extend past the top of the slab
  let { top: brickLedgeTopMax } = makeStemWallExtrusionSize({
    foundationPath,
    level,
    stemWall,
    slabSettings,
    footing,
  });
  const slabType = slabTypeAtFoundationPath({ condition: foundationPath.condition }, slabSettings);
  if (slabType !== SlabType.NONE) {
    brickLedgeTopMax = Math.max(brickLedgeTopMax, floorPathTop(level, floorPath));
  }

  const { alignment: parentAlignment } = foundationPathOffsetAlignment({
    foundationPath,
    floorPath,
  });

  const brickLedgeOffset =
    parentAlignment === FoundationSectionAlignment.CENTER
      ? stemWallWidth({ path: foundationPath, stemWall }).width / 2
      : 0;

  return makeBrickLedgeSize({
    level,
    foundationPath,
    floorPath,
    sectionComponent: stemWall,
    pathEnds,
    brickLedgeType,
    brickLedgeBehavior,
    brickLedgeWidth,
    brickLedgeDepthModifier,
    brickLedgeTopMax,
    brickLedgeOffset,
    doorSettings,
    isolatedFoundationSectionSizes,
    material,
    planarGraphs,
  });
}

function stemWallSuperSystemBottomWithoutFloorPath(
  foundationPath: Pick<FoundationPath, "condition">,
  stemTop: number,
  slabSettings: SlabSettings,
): number {
  // Align the slab split with the resolved stem top (same anchor stack as stemWallTop).
  const slabType = slabTypeAtFoundationPath(foundationPath, slabSettings);
  const slabDepth = slabDepthAtFoundationPath(foundationPath, slabSettings);
  if (slabType !== SlabType.NONE && slabDepth !== 0) {
    return stemTop - slabDepth;
  }

  return stemTop;
}

function splitStemWallAtBOSuperSystem(
  level: Level,
  foundationPath: FoundationPath,
  stemWallSize: FoundationExtrusionSize,
  slabSettings: SlabSettings | undefined,
  framingComponents: Table<FramingComponentEntity>,
  slabComponents: Table<SlabComponentEntity>,
  sheathingComponents: Table<SheathingComponentEntity>,
  floorPath?: FloorPath,
) {
  const { top, depth } = stemWallSize;
  const bottom = top - depth;

  let split: number;
  if (floorPath) {
    split = foundationPathSuperSystemBottom({
      foundationPath,
      floorPath,
      level,
      slabSettings,
      framingComponents,
      slabComponents,
      sheathingComponents,
    });
  } else if (foundationPath.isDerivedFromHorizontalAssembly && slabSettings) {
    split = stemWallSuperSystemBottomWithoutFloorPath(foundationPath, top, slabSettings);
  } else {
    split = top;
  }

  let topSplit: FoundationExtrusionSize | undefined = foundationExtrusionSizeNew(top, Math.abs(top - split));
  let bottomSplit: FoundationExtrusionSize | undefined = foundationExtrusionSizeNew(split, Math.abs(split - bottom));

  if (topSplit.depth < DIST_EPSILON) {
    topSplit = undefined;
  }

  if (bottomSplit.depth < DIST_EPSILON) {
    bottomSplit = undefined;
  }

  return { topSplit, bottomSplit };
}

function stemWallSectionId(
  componentType: FoundationComponentType,
  sectionType: FoundationSectionType,
  condition: FoundationCondition,
) {
  let sectionCode = RoomClass[RoomClass.HOUSE];
  if (isGarageFoundationCondition[condition]) {
    sectionCode = RoomClass[RoomClass.GARAGE];
  } else if (isPorchFoundationCondition[condition] || isDeckFoundationCondition[condition]) {
    sectionCode = RoomClass[RoomClass.PORCH];
  }
  return foundationSectionId(componentType, sectionType, sectionCode);
}

function makeStemWallExtrusionSize(stemWallProps: {
  foundationPath: FoundationPath;
  stemWall: StemWallEntity;
  level: Level;
  slabSettings: SlabSettings | undefined;
  footing: StripFootingEntity;
}): FoundationExtrusionSize {
  const top = stemWallTop(stemWallProps);
  const bottom = stemWallBottom({
    foundationPath: stemWallProps.foundationPath,
    footing: stemWallProps.footing,
    level: stemWallProps.level,
  });
  const depth = Math.abs(top - bottom);

  return { top, depth };
}

function stemWallBottom({
  foundationPath,
  footing,
  level,
}: {
  foundationPath: Pick<FoundationPath, "condition" | "datums"> & Partial<Pick<FoundationPath, "assembly">>;
  footing: StripFootingEntity;
  level: Level;
}) {
  const datumAbstraction =
    foundationPath.assembly?.bottomDatumAbstraction ?? defaultFoundationDatumAbstractions.bottomDatum;

  let bottom = datumAbstraction.resolveVerticalDatum({
    level,
    path: foundationPath,
  });

  bottom = worldInchesNew(bottom + footing.footingDepth);

  return bottom;
}

export function stemWallTop({
  foundationPath,
  stemWall,
  level,
  slabSettings,
}: {
  foundationPath: Pick<FoundationPath, "condition" | "datums"> &
    Partial<Pick<FoundationPath, "assembly" | "isDerivedFromHorizontalAssembly">>;
  stemWall: StemWallEntity;
  level: Level;
  slabSettings: SlabSettings | undefined;
}) {
  let topDatum = stemWallTopDatum({
    path: foundationPath,
    stemWall,
    slabSettings,
    level,
  });

  // these offset merges are added to support the existing behavior, were the offset can be obtined from
  // the stemWallTopDatum if not defined by the assembly override
  if (foundationPath.assembly?.topDatumAbstraction) {
    topDatum = mergeDatumOffset(foundationPath.assembly.topDatumAbstraction.getDatumEntity(), topDatum.offset);
    topDatum = mergeDatumOffset(topDatum, foundationPath.assembly.topDatumAbstraction.getDatumEntity().offset);
  }

  const datumAbstraction = createDatumAbstraction(topDatum) as FoundationVerticalDatumAbstraction;

  let top = datumAbstraction.resolveVerticalDatum({
    level,
    path: foundationPath,
  });

  //eliminate system constraints if the FoundationPath is derived from the HorizontalAssembly
  if (top > level.floorElevation + level.topOfWall && !foundationPath.isDerivedFromHorizontalAssembly) {
    top = worldInchesNew(level.floorElevation + level.topOfWall);
  }

  return top;
}

function stemWallTopDatum({
  path,
  stemWall,
  slabSettings,
  level,
}: {
  path: Pick<FoundationPath, "condition">;
  stemWall: Pick<StemWallEntity, "stemWallType">;
  slabSettings: SlabSettings | undefined;
  level: Level;
}): FoundationVerticalDatumEntity {
  if (!slabSettings) {
    return foundationVerticalDatumEntityNew({ anchor: FoundationDatumVerticalAnchor.BOTTOM_OF_FLOORPLATE });
  }
  const { condition } = path;
  const { stemWallType } = stemWall;
  const { interiorSlabType, basementSlabType } = slabSettings;
  const { floorPlateThickness } = level;

  if (isSpanFoundationCondition[condition]) {
    if (slabTypeAtFoundationPath(path, slabSettings) === SlabType.NONE) {
      return foundationVerticalDatumEntityNew({ anchor: FoundationDatumVerticalAnchor.BOTTOM_OF_FLOORPLATE });
    } else {
      return foundationVerticalDatumEntityNew({ anchor: FoundationDatumVerticalAnchor.BOTTOM_OF_SLAB });
    }
  }

  if (basementSlabType !== SlabType.NONE && isBasementFoundationCondition[condition]) {
    return foundationVerticalDatumEntityNew({ anchor: FoundationDatumVerticalAnchor.LEVEL_TOP_OF_WALL });
  }

  if (isDeckFoundationCondition[condition]) {
    return foundationVerticalDatumEntityNew({ anchor: FoundationDatumVerticalAnchor.BOTTOM_OF_FLOORPLATE });
  }

  if (interiorSlabType === SlabType.NONE) {
    if (isInteriorFoundationCondition[condition]) {
      return foundationVerticalDatumEntityNew({ anchor: FoundationDatumVerticalAnchor.BOTTOM_OF_FLOORPLATE });
    } else if (isGarageFoundationCondition[condition]) {
      return foundationVerticalDatumEntityNew({
        anchor: FoundationDatumVerticalAnchor.LEVEL_FLOOR_ELEVATION,
        offset: -floorPlateThickness,
      });
    } else if (isPorchFoundationCondition[condition]) {
      return foundationVerticalDatumEntityNew({ anchor: FoundationDatumVerticalAnchor.BOTTOM_OF_SLAB });
    }
  }

  switch (stemWallType) {
    case StemWallType.CMU: {
      if (isInteriorFoundationCondition[condition]) {
        return foundationVerticalDatumEntityNew({ anchor: FoundationDatumVerticalAnchor.BOTTOM_OF_SLAB });
      } else if (isGarageFoundationCondition[condition]) {
        return foundationVerticalDatumEntityNew({ anchor: FoundationDatumVerticalAnchor.LEVEL_FLOOR_ELEVATION });
      } else if (isPorchFoundationCondition[condition]) {
        return foundationVerticalDatumEntityNew({ anchor: FoundationDatumVerticalAnchor.BOTTOM_OF_SLAB });
      } else {
        return foundationVerticalDatumEntityNew({ anchor: FoundationDatumVerticalAnchor.BOTTOM_OF_SLAB });
      }
    }
    case StemWallType.CIP: {
      if (isInteriorFoundationCondition[condition]) {
        return foundationVerticalDatumEntityNew({ anchor: FoundationDatumVerticalAnchor.LEVEL_FLOOR_ELEVATION });
      } else if (isGarageFoundationCondition[condition]) {
        return foundationVerticalDatumEntityNew({ anchor: FoundationDatumVerticalAnchor.LEVEL_FLOOR_ELEVATION });
      } else if (isPorchFoundationCondition[condition]) {
        return foundationVerticalDatumEntityNew({ anchor: FoundationDatumVerticalAnchor.BOTTOM_OF_SLAB });
      } else {
        return foundationVerticalDatumEntityNew({ anchor: FoundationDatumVerticalAnchor.BOTTOM_OF_SLAB });
      }
    }
    default: {
      assertUnreachable(stemWallType);
    }
  }
}

export function stemWallWidth({
  path,
  stemWall,
}: {
  path: Pick<FoundationPath, "condition">;
  stemWall: StemWallEntity;
}): {
  width: number;
} {
  const { condition } = path;
  const { stemWallType, brickLedgeType, brickLedgeBehavior } = stemWall;

  let width = 0;

  switch (stemWallType) {
    case StemWallType.CMU: {
      width = stemWall.stemWallBlockWidth;
      break;
    }
    case StemWallType.CIP: {
      width = stemWall.stemWallWidth;
      const brickType = brickLedgeTypeAtCondition(condition, brickLedgeType, brickLedgeBehavior);
      if (brickType === BrickLedgeType.INSET) {
        width -= stemWall.brickLedgeWidth;
      }
      break;
    }
    default: {
      assertUnreachable(stemWallType);
    }
  }

  return { width };
}

export function stemWallBrickLedgeWidth({
  path,
  stemWall,
}: {
  path: Pick<FoundationPath, "condition">;
  stemWall: StemWallEntity;
}) {
  const { condition } = path;
  const { stemWallType, brickLedgeType, brickLedgeBehavior } = stemWall;

  if (brickLedgeTypeAtCondition(condition, brickLedgeType, brickLedgeBehavior) === BrickLedgeType.NONE) {
    return 0;
  }

  if (stemWallType === StemWallType.CIP) {
    return stemWall.brickLedgeWidth;
  }

  // For CMU, return the difference between brick ledge width and stem wall width
  return stemWall.stemWallBrickLedgeBlockWidth - stemWall.stemWallBlockWidth;
}

export function stemWallBaseWidth({
  stemWall,
}: {
  // path: Pick<FoundationPath, "condition">;
  stemWall: StemWallEntity;
}) {
  const { stemWallType } = stemWall;

  let width = 0;

  switch (stemWallType) {
    case StemWallType.CMU: {
      width = stemWall.stemWallBlockWidth;
      break;
    }
    case StemWallType.CIP: {
      width = stemWall.stemWallWidth;
      break;
    }
    default: {
      assertUnreachable(stemWallType);
    }
  }

  return width;
}

function stemWallOpenings({
  foundationPath,
  floorPath,
  doorSettings,
  isolatedFoundationSectionSizes = [],
  planarGraphs,
}: {
  foundationPath: FoundationPath;
  floorPath?: FloorPath;
  doorSettings: DoorSettings;
  isolatedFoundationSectionSizes?: IsolatedFoundationSectionSize[];
  planarGraphs: Table<PolygonPlanarGraph>;
}): {
  openings: FoundationSectionOpeningDescriptor[];
} {
  return foundationPathOpenings({
    foundationPath,
    floorPath,
    doorSettings,
    isolatedFoundationSectionSizes,
    includeDoors: true,
    includeWindows: true,
    planarGraphs,
  });
}

export function isStemWallInsetSection(section: FoundationSection): boolean {
  const {
    size: { sectionComponent },
    path: { condition },
  } = section;
  if (sectionComponent.componentType !== FoundationComponentType.STEMWALL) {
    return false;
  }
  const stemWall = sectionComponent as StemWallEntity;
  return (
    stemWall.stemWallType === StemWallType.CIP &&
    brickLedgeTypeAtCondition(condition, stemWall.brickLedgeType, stemWall.brickLedgeBehavior) === BrickLedgeType.INSET
  );
}
